import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { Post, PostInput, POST_SELECT } from '../types/post';

/**
 * Eventi, novità e articoli dalla tabella posts. La RLS fa il filtro: i
 * visitatori ricevono solo quelli pubblicati, un admin con 2FA anche le bozze.
 */
export function usePosts() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const { data, error } = await supabase.from('posts').select(POST_SELECT).order('created_at', { ascending: false });
    if (error) {
      console.error('Error fetching posts:', error);
      setError('Non è stato possibile caricare eventi e novità');
    } else {
      setError(null);
      setPosts((data ?? []) as Post[]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  /** Solo admin con 2FA. Restituisce il contenuto salvato. */
  const savePost = async (id: string | null, input: PostInput): Promise<Post> => {
    const row = { ...input, updated_at: new Date().toISOString() };
    const query = id ? supabase.from('posts').update(row).eq('id', id) : supabase.from('posts').insert(row);
    const { data, error } = await query.select(POST_SELECT);
    if (error) {
      if (error.code === '23505') throw new Error('Esiste già un contenuto con questo indirizzo: cambialo');
      throw new Error(error.message);
    }
    // Con RLS un salvataggio non autorizzato non dà errore: non tocca nessuna riga.
    if (!data || data.length === 0) throw new Error('Non autorizzato a modificare eventi e novità');
    const saved = data[0] as Post;
    setPosts((prev) => (id ? prev.map((p) => (p.id === id ? saved : p)) : [saved, ...prev]));
    return saved;
  };

  const deletePost = async (id: string) => {
    const { data, error } = await supabase.from('posts').delete().eq('id', id).select('id');
    if (error) throw new Error(error.message);
    if (!data || data.length === 0) throw new Error('Non autorizzato a eliminare eventi e novità');
    setPosts((prev) => prev.filter((p) => p.id !== id));
  };

  return { posts, loading, error, refresh, savePost, deletePost };
}

/** Un contenuto per indirizzo; `notFound` se non esiste o la RLS non lo restituisce. */
export function usePost(slug: string | undefined) {
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoading(true);
    supabase
      .from('posts')
      .select(POST_SELECT)
      .eq('slug', slug)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) console.error('Error fetching post:', error);
        setPost((data as Post | null) ?? null);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return { post, loading, notFound: !loading && !post };
}
